import React, { useMemo, useState, useEffect } from "react";
import Typography from "@mui/material/Typography";
import Link from "@mui/material/Link";
import BreadcrumbsComponent from "../components/Breadcrumbs";
import { Box, Button, Chip, CircularProgress } from "@mui/material";
import { routes } from "../routes/routes";
import DataTableGeneric from "../components/DataTableGeneric";
import AddIcon from "@mui/icons-material/Add";
import DescriptionIcon from "@mui/icons-material/Description";
import { ErrorSnackbar } from "../components/Snackbar/ErrorSnackbar";
import { SuccessSnackbar } from "../components/Snackbar/SuccessSnackbar";
import { useNavigate } from "react-router-dom";
import SearchInput from "../components/SearchInput";
import { ToggleButtonGroup, ToggleButton } from "@mui/material";
import useFetchAnswerTypes from "../hooks/useFetchAnswerTypes";
import useFetchAnswerOptions from "../hooks/useFetchAnswerOptions";
import useImportAnswerTypesOptionsFromExcel from "../hooks/useImportAnswerTypesOptionsFromExcel";
import BulkLoadModal from "../components/Modal/BulkLoadModal";
import BulkLoadResultsModal from "../components/Modal/BulkLoadResultsModal";
import EditAnswerOptionModal from "../components/Modal/EditAnswerOptionModal";
import EditAnswerTypeModal from "../components/Modal/EditAnswerTypeModal";
import useUpdateAnswerType from "../hooks/useUpdateAnswerType";
import useUpdateAnswerOption from "../hooks/useUpdateAnswerOption";
import useDeleteAnswerOption from "../hooks/useDeleteAnswerOption";
import useDeleteAnswerType from "../hooks/useDeleteAnswerType";
import ConfirmationModal from "../components/Modal/ConfirmationModal";

// Columnas
const responseTypesColumns = [
  { id: "id", label: "ID" },
  { id: "externalId", label: "ID Externo" },
  { id: "name", label: "Nombre" },
  { id: "options", label: "Opciones de respuesta" },
  { id: "actions", label: "Acciones" },
];

const responseOptionsColumns = [
  { id: "id", label: "ID" },
  { id: "option", label: "Opción" },
  { id: "label", label: "Etiqueta" },
  { id: "position", label: "Posición" },
  { id: "answerTypeExternal", label: "ID Externo del tipo" },
  { id: "actions", label: "Acciones" },
];

const AnswerTypesOptionsPage = () => {
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState("types");
  const [searchTerm, setSearchTerm] = useState("");
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [showSuccess, setShowSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");

  const { updateAnswerOption, loading: updatingOption } = useUpdateAnswerOption();

  const { deleteAnswerType, loading: deletingType } = useDeleteAnswerType();

  const { deleteAnswerOption, loading: deletingOption } = useDeleteAnswerOption();

  const { data: typesData = [], error: typesError, loading: loadingTypes, refetch: refetchTypes } = useFetchAnswerTypes();

  const { data: optionsData = [], error: optionsError, loading: loadingOptions, refetch: refetchOptions } = useFetchAnswerOptions();

  const { importFromExcel, loading: importLoading } = useImportAnswerTypesOptionsFromExcel();

  const { updateAnswerType, loading: updatingType } = useUpdateAnswerType();

  const [showImportModal, setShowImportModal] = useState(false);
  const [showResultsModal, setShowResultsModal] = useState(false);
  const [excelResult, setExcelResult] = useState(null);

  const [editOpen, setEditOpen] = useState(false);
  const [currentOpt, setCurrentOpt] = useState(null);

  const [editTypeOpen, setEditTypeOpen] = useState(false);
  const [currentType, setCurrentType] = useState(null);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [toDelete, setToDelete] = useState(null);

  const [confirmTypeOpen, setConfirmTypeOpen] = useState(false);
  const [typeToDelete, setTypeToDelete] = useState(null);

  const TEMPLATE_URL = `${process.env.PUBLIC_URL || ""}/plantillas/crear_tipos_opciones_respuesta_plantilla.xlsx`;

  const askDeleteOption = (opt) => {
    setToDelete(opt);
    setConfirmOpen(true);
  };

  const askDeleteType = (type) => {
    setTypeToDelete(type);
    setConfirmTypeOpen(true);
  };

  const handleConfirmDeleteType = async () => {
    if (!typeToDelete) return;
    try {
      await deleteAnswerType(typeToDelete.id);
      setConfirmTypeOpen(false);
      setTypeToDelete(null);

      await Promise.allSettled([refetchTypes(), refetchOptions()]);

      setSuccessMessage("Tipo de respuesta eliminado correctamente.");
      setShowSuccess(true);
    } catch (e) {
      const status = e?.backend?.status ?? e?.status;
      let msg = e?.message || "No se pudo eliminar el tipo de respuesta.";
      if (status === 409) {
        msg = "El tipo está en uso (por opciones o preguntas asociadas) y no puede eliminarse.";
      }
      setErrorMessage(msg);
      setShowError(true);
    }
  };

  const handleConfirmDeleteOption = async () => {
    if (!toDelete) return;
    try {
      await deleteAnswerOption(toDelete.id);
      setConfirmOpen(false);
      setToDelete(null);

      await Promise.allSettled([refetchOptions(), refetchTypes()]);

      setSuccessMessage("Opción eliminada correctamente.");
      setShowSuccess(true);
    } catch (e) {
      setErrorMessage(e?.message || "No se pudo eliminar la opción.");
      setShowError(true);
    }
  };

  const openEditType = (type) => {
    setCurrentType({
      id: type.id,
      name: type.name,
      external_id: type.external_id,
      options: Array.isArray(type.options) ? type.options : [],
    });
    setEditTypeOpen(true);
  };

  const handleUpdateType = async ({ id, name, external_id }) => {
    try {
      await updateAnswerType(id, { name, external_id });
      await Promise.allSettled([refetchTypes(), refetchOptions()]);

      setSuccessMessage("Tipo de respuesta actualizado correctamente.");
      setShowSuccess(true);
    } catch (e) {
      setErrorMessage(e?.message || "No se pudo actualizar el tipo de respuesta.");
      setShowError(true);

      throw e;
    }
  };
  const openEdit = (opt) => {
    setCurrentOpt({
      id: opt.id,
      option: opt.option,
      label: opt.label,
      position: opt.position,
      answerType: {
        id: opt?.answer_type?.id,
        external_id: opt?.answer_type?.external_id,
        name: opt?.answer_type?.name,
      },
    });
    setEditOpen(true);
  };

  const handleQuickEditOptionFromType = (opt, typeCtx) => {
    setCurrentOpt({
      id: opt.id,
      option: opt.option,
      label: opt.label,
      position: opt.position,
      answerType: {
        id: typeCtx?.id,
        external_id: typeCtx?.external_id,
        name: typeCtx?.name,
      },
    });
    setEditTypeOpen(false);
    setTimeout(() => setEditOpen(true), 0);
  };

  const handleUpdateOption = async (payload) => {
    try {
      await updateAnswerOption(payload.id, {
        option: payload.option,
        label: payload.label,
        position: payload.position,
      });

      await Promise.allSettled([refetchOptions(), refetchTypes()]);

      setSuccessMessage("Opción actualizada correctamente.");
      setShowSuccess(true);
    } catch (e) {
      setErrorMessage(e?.message || "No se pudo actualizar la opción.");
      setShowError(true);
      throw e;
    }
  };

  // Manejo de errores
  useEffect(() => {
    const err = typesError || optionsError;
    if (err) {
      setErrorMessage(err?.message || "Error al cargar los datos.");
      setShowError(true);
    }
  }, [typesError, optionsError]);

  const normalizedQuery = searchTerm.trim().toLowerCase();

  const typeRows = useMemo(() => {
    const filtered = (typesData || []).filter((t) => {
      if (!normalizedQuery) return true;
      const byName = (t?.name || "").trim().toLowerCase().includes(normalizedQuery);
      const byExternal = (t?.external_id || "").trim().toLowerCase().includes(normalizedQuery);
      return byName || byExternal;
    });

    return filtered.map((item) => {
      const opts = Array.isArray(item?.options) ? item.options : [];

      return {
        id: item.id,
        externalId: item.external_id,
        name: item.name?.trim() || "[Sin nombre]",
        options: opts.length ? (
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, py: 1, overflow: "hidden", whiteSpace: "normal", overflowWrap: "anywhere" }}>
            {opts
              .slice()
              .sort((a, b) => (a.position || 0) - (b.position || 0))
              .map((opt) => (
                <Chip key={opt.id} size="small" label={`${opt.position ?? "?"}. ${opt.label ?? ""} (${opt.option ?? ""})`} />
              ))}
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary">
            [Sin opciones]
          </Typography>
        ),
        actions: (
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button size="small" variant="contained" onClick={() => openEditType(item)}>
              Editar
            </Button>
            <Button size="small" variant="outlined" color="error" onClick={() => askDeleteType(item)} disabled={deletingType}>
              Eliminar
            </Button>
          </Box>
        ),
      };
    });
  }, [typesData, normalizedQuery, deletingType]);
  // Filas
  const optionRows = useMemo(() => {
    const filtered = (optionsData || []).filter((opt) => {
      if (!normalizedQuery) return true;
      const byTypeExternal = (opt?.answer_type?.external_id || "").trim().toLowerCase().includes(normalizedQuery);
      const byLabel = (opt?.label || "").trim().toLowerCase().includes(normalizedQuery);
      return byTypeExternal || byLabel;
    });

    return filtered.map((opt) => ({
      id: opt.id,
      option: opt.option,
      label: opt.label,
      position: opt.position,
      answerTypeExternal: opt?.answer_type?.external_id || "",
      actions: (
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button size="small" variant="contained" onClick={() => openEdit(opt)}>
            Editar
          </Button>
          <Button size="small" variant="outlined" color="error" onClick={() => askDeleteOption(opt)} disabled={deletingOption}>
            Eliminar
          </Button>
        </Box>
      ),
    }));
  }, [optionsData, normalizedQuery, deletingOption]);

  const loading = activeTab === "types" ? loadingTypes : loadingOptions;

  const handleOpenImport = () => setShowImportModal(true);

  const handleImportFromExcel = async (file) => {
    const result = await importFromExcel(file);

    if (result?.errorMessage) {
      setErrorMessage(result.errorMessage.message || "Hubo un error al procesar el archivo.");
      setShowError(true);
      return false;
    }

    if (result?.data?.error_details) {
      const encabezadoErr = (result.data.error_details || []).find((e) => {
        const msg = (e?.message || e?.error || "").toString().toLowerCase();
        return msg.includes("encabezado incorrecto") || msg.includes("estructura");
      });
      if (encabezadoErr) {
        setErrorMessage("El archivo no contiene la estructura correcta.");
        setShowError(true);
        return false;
      }
    }

    if (result?.data) {
      setExcelResult(result.data);
      setShowResultsModal(true);
      return true;
    }

    setErrorMessage("No se pudo procesar el archivo.");
    setShowError(true);
    return false;
  };

  const handleCloseResults = async () => {
    setShowResultsModal(false);

    if ((excelResult?.created ?? 0) > 0 || (excelResult?.updated ?? 0) > 0) {
      await Promise.allSettled([refetchTypes(), refetchOptions()]);
    }
  };

  return (
    <div className="min-w-full p-10 overflow-x-auto">
      <BreadcrumbsComponent
        breadcrumbs={[
          <Link underline="none" key="1" color="inherit" href={routes.root}>
            Página Principal
          </Link>,
          <Typography key="2" color="secondary" sx={{ fontSize: "14px" }}>
            Tipos y opciones de respuesta
          </Typography>,
        ]}
      />
      {/* Toggle */}
      <Box sx={{ display: "flex", justifyContent: "center", my: 3 }}>
        <ToggleButtonGroup
          value={activeTab}
          exclusive
          onChange={(_, val) => val && setActiveTab(val)}
          aria-label="modo de visualización"
          sx={{
            bgcolor: "background.paper",
            borderRadius: 1.5,
            border: "1px solid",
            borderColor: "divider",
            height: 36,
          }}
        >
          <ToggleButton value="types" aria-label="ver tipos" sx={{ px: 2.5, py: 0.5, textTransform: "none", fontSize: "0.875rem" }}>
            Tipos
          </ToggleButton>
          <ToggleButton value="options" aria-label="ver opciones" sx={{ px: 2.5, py: 0.5, textTransform: "none", fontSize: "0.875rem" }}>
            Opciones
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>
      {/* Encabezado */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 4 }}>
        <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: "#333333" }}>
          {activeTab === "types" ? "Tipos de respuesta" : "Opciones de respuesta"}
        </Typography>

        <Box gap={2} sx={{ display: "flex", alignItems: "center" }}>
          <Button variant="outlined" startIcon={<AddIcon />} sx={{ height: 40 }} disabled={importLoading} onClick={handleOpenImport}>
            {importLoading ? "Importando..." : "Importar tipos y opciones de respuesta"}
          </Button>
        </Box>
      </Box>
      {/* Buscador */}
      <Box sx={{ mt: 2 }}>
        <Box sx={{ mb: 3 }}>
          <SearchInput
            id="search-response-types"
            placeholder={
              activeTab === "types" ? "Buscar por nombre o ID externo del tipo de respuesta..." : "Buscar por ID externo del tipo o etiqueta..."
            }
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </Box>

        {/* Tabla */}
        {loading ? (
          <div className="flex justify-center items-center h-40">
            <CircularProgress />
          </div>
        ) : (
          <DataTableGeneric
            columns={activeTab === "types" ? responseTypesColumns : responseOptionsColumns}
            rows={activeTab === "types" ? typeRows : optionRows}
          />
        )}
      </Box>
      {/* Snackbars */}
      <ErrorSnackbar open={showError} onClose={() => setShowError(false)} title="Ha ocurrido un error" message={errorMessage} />
      <SuccessSnackbar open={showSuccess} onClose={() => setShowSuccess(false)} title="Completado" message={successMessage} />
      {/* Modal de resultados de carga por Excel */}
      <BulkLoadResultsModal
        open={showResultsModal}
        onClose={handleCloseResults}
        result={excelResult}
        title="Resultado de importación"
        labels={{
          total: "Total filas",
          created: "Creados",
          updated: "Actualizados",
          failed: "Errores",
          skipped: "Sin cambios",
        }}
        keys={{
          total: "total",
          created: "created",
          updated: "updated",
          failed: "failed",
          errorDetails: "error_details",
          skipped: "skipped",
        }}
      />
      {/* Modal de carga de archivo Excel */}
      <BulkLoadModal
        open={showImportModal}
        onClose={() => setShowImportModal(false)}
        onSelectFile={handleImportFromExcel}
        title="Importar tipos y opciones por Excel"
        description="Descargue la plantilla requerida, complétela y súbala para crear los tipos y opciones de respuesta."
        template={{ url: TEMPLATE_URL, label: "Descargar plantilla" }}
        accept=".xlsx"
        maxSizeMB={10}
        uploadButtonLabel={importLoading ? "Importando..." : "Subir y procesar"}
        icon={<DescriptionIcon fontSize="large" color="tertiary" />}
        warnings={["El archivo debe ser .xlsx", "Las preguntas de tipo abierta no llevan opciones"]}
        closeOnSuccess={true}
      />
      <EditAnswerTypeModal
        open={editTypeOpen}
        onClose={() => setEditTypeOpen(false)}
        initialData={currentType}
        onSubmit={handleUpdateType}
        onManageOptions={() => {
          setActiveTab("options");
          setEditTypeOpen(false);
          setSearchTerm(currentType?.external_id || "");
        }}
        onQuickEditOption={handleQuickEditOptionFromType}
        loading={updatingType}
      />
      <EditAnswerOptionModal
        open={editOpen}
        onClose={() => setEditOpen(false)}
        initialData={currentOpt}
        onSubmit={handleUpdateOption}
        loading={updatingOption}
      />{" "}
      <ConfirmationModal
        open={confirmOpen}
        handleClose={() => {
          setConfirmOpen(false);
          setToDelete(null);
        }}
        handleConfirm={handleConfirmDeleteOption}
        title="Eliminar opción"
        description={toDelete ? `¿Desea eliminar la opción “${toDelete.label}” (${toDelete.option})?` : "¿Desea eliminar esta opción?"}
        confirmText={deletingOption ? "Eliminando..." : "Eliminar"}
        confirmColor="error"
      />
      <ConfirmationModal
        open={confirmTypeOpen}
        handleClose={() => {
          setConfirmTypeOpen(false);
          setTypeToDelete(null);
        }}
        handleConfirm={handleConfirmDeleteType}
        title="Eliminar tipo de respuesta"
        description={
          typeToDelete ? `¿Desea eliminar el tipo “${typeToDelete.name}” (${typeToDelete.external_id})?` : "¿Desea eliminar este tipo de respuesta?"
        }
        confirmText={deletingType ? "Eliminando..." : "Eliminar"}
        confirmColor="error"
      />
    </div>
  );
};

export default AnswerTypesOptionsPage;
